import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wifi, Shield, Laptop, Zap, Settings, ShoppingCart, Wrench, GraduationCap } from 'lucide-react';
import './ItServices.css';

const ItServices = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="it-services-page">
      {/* Hero Section */}
      <section className="it-services-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container">
            <h1 className="display-large animated">IT Services</h1>
            <p className="body-large animated">
              Comprehensive IT solutions for remote, rural, and off-grid environments
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="it-detailed-section">
        <div className="container">
          <div className="section-header animated">
            <h2 className="heading-1">Our IT Service Categories</h2>
            <p className="body-large">
              End-to-end technology services designed for areas with limited infrastructure
            </p>
          </div>

          <div className="it-services-grid">
            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Zap size={40} />
              </div>
              <h2 className="heading-2">Power-Independent IT Services</h2>
              <p className="service-subtitle">Solar & Backup Power for IT</p>
              <ul className="it-service-list">
                <li>Solar power systems for homes, schools, clinics & SMEs</li>
                <li>UPS & inverter systems for routers, Wi-Fi, CCTV, computers & servers</li>
                <li>Load-shedding protection setups</li>
                <li>Power audits & energy optimization</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Wifi size={40} />
              </div>
              <h2 className="heading-2">Off-Grid Internet & Connectivity</h2>
              <p className="service-subtitle">Internet Solutions</p>
              <ul className="it-service-list">
                <li>Starlink installation & maintenance</li>
                <li>Long-range Wi-Fi (point-to-point & point-to-multipoint)</li>
                <li>Community Wi-Fi hotspots</li>
                <li>Farm & rural internet setups</li>
                <li>Mesh networks for villages & campuses</li>
              </ul>
              <div className="service-subsection">
                <h3 className="heading-3">Networking</h3>
                <ul className="it-service-list">
                  <li>Router configuration (MikroTik, Ubiquiti, TP-Link)</li>
                  <li>Bandwidth management & caching</li>
                  <li>Network security (firewalls, VPNs)</li>
                </ul>
              </div>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Shield size={40} />
              </div>
              <h2 className="heading-2">Off-Grid Security & Surveillance</h2>
              <p className="service-subtitle">Security & Alarm Systems</p>
              <ul className="it-service-list">
                <li>Solar-powered CCTV systems</li>
                <li>Wireless camera installations</li>
                <li>Access control (biometric & RFID)</li>
                <li>GSM-based alarm systems</li>
                <li>Remote monitoring</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Laptop size={40} />
              </div>
              <h2 className="heading-2">IT Support for Remote & Rural Areas</h2>
              <p className="service-subtitle">On-site Technical Support</p>
              <ul className="it-service-list">
                <li>On-site IT support for schools & clinics</li>
                <li>Device setup (laptops, tablets, POS systems)</li>
                <li>Offline-friendly software installation</li>
                <li>Virus removal & system optimization</li>
                <li>Hybrid data backup & recovery</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Settings size={40} />
              </div>
              <h2 className="heading-2">Digital Services for SMEs</h2>
              <ul className="it-service-list">
                <li>Offline-capable POS systems</li>
                <li>Inventory & accounting software</li>
                <li>Business email & cloud storage</li>
                <li>Low-power website hosting</li>
                <li>Mobile payment integrations</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <GraduationCap size={40} />
              </div>
              <h2 className="heading-2">Training & Capacity Building</h2>
              <p className="service-subtitle">Training Services</p>
              <ul className="it-service-list">
                <li>Basic computer literacy (offline curriculum)</li>
                <li>Solar system user training</li>
                <li>Network & CCTV maintenance training</li>
                <li>Cybersecurity awareness workshops</li>
                <li>Youth tech skills development</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <Wrench size={40} />
              </div>
              <h2 className="heading-2">Maintenance Contracts</h2>
              <p className="service-subtitle">Support Plans</p>
              <ul className="it-service-list">
                <li>Monthly IT support retainers</li>
                <li>Solar system maintenance</li>
                <li>CCTV & network health checks</li>
                <li>Remote low-bandwidth support</li>
              </ul>
            </div>

            <div className="it-service-card animated">
              <div className="service-icon-large">
                <ShoppingCart size={40} />
              </div>
              <h2 className="heading-2">Hardware Sales & Rentals</h2>
              <p className="service-subtitle">Equipment</p>
              <ul className="it-service-list">
                <li>Refurbished laptops & desktops</li>
                <li>Solar kits (routers, lights, CCTV)</li>
                <li>Networking equipment</li>
                <li>Device leasing for schools & startups</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="it-services-cta-section">
        <div className="container">
          <div className="cta-box animated">
            <h2 className="heading-1">Need IT Support?</h2>
            <p className="body-large">Contact us for a free consultation on your IT needs</p>
            <div className="cta-contact-info">
              <a href="mailto:offgridsolution@gmail.com" className="contact-link">
                <span className="contact-label">Email:</span> offgridsolution@gmail.com
              </a>
              <a href="https://wa.me/27724603624" target="_blank" rel="noopener noreferrer" className="contact-link whatsapp-link">
                <Phone size={20} />
                <span className="contact-label">WhatsApp:</span> +27 72 460 3624
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ItServices;
